"use client";

import { FC, useMemo } from "react";
import classNames from "classnames";
import { ITicketResponse } from "@finbackoffice/clientbff-client";
import { AssetsDirectory, TicketStatus, TicketType, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { useDate, DEFAULT_DATE_FORMAT } from "hooks";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import { Svg } from "components/base/svg/Svg";
import styles from "./sport-bet-row.module.sass";

interface IProps {
    bet: ITicketResponse;
    showDetailsButton?: boolean;
    onDetailsClick?: (val: ITicketResponse) => void;
}

const SportOtherBetView: FC<IProps> = ({ bet, showDetailsButton, onDetailsClick }) => {
    const { formatDate } = useDate();

    const renderMultibetBonus = useMemo(() => {
        const [, decimal] = bet.bonus.split(".");
        const hasDecimal = decimal && parseInt(decimal) > 0;

        return (
            <>
                {` / `}
                <Translate
                    tid="multibet_bonus"
                    replace={{
                        amount: hasDecimal ? parseFloat(bet.bonus) : parseInt(bet.bonus),
                    }}
                />
            </>
        );
    }, [bet.bonus]);

    return (
        <div
            className={classNames(
                styles.bet,
                styles.betExpress,
                styles[`base-icon-${bet.status.toLowerCase()}`],
            )}
            onClick={() => onDetailsClick?.(bet)}>
            <div>
                <span className={styles.label}>
                    <Translate tid="account_bets_id" namespace={TranslationScopes.UserAccount} />:
                </span>
                <strong>{bet.short_id}</strong>
                <span className={styles.label}>
                    {formatDate(new Date(bet.created_at), `${DEFAULT_DATE_FORMAT} HH:mm`, false)}
                </span>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/status-${bet.status.toLowerCase()}.svg`}
                    className="svg-icon"
                />
            </div>
            <div className={styles.totalBets}>
                {bet.type === TicketType.Express && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/my-bets-multiple.svg`}
                        className={classNames("svg-icon", styles.icon)}
                    />
                )}
                {bet.type === TicketType.System && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/my-bets-system.svg`}
                        className={classNames("svg-icon", styles.icon)}
                    />
                )}
                <Translate tid={`betType_${bet.type.toLowerCase()}`} />
                {bet.type === TicketType.System && ` (${bet.system_count}/${bet.bets.length})`}
                {bet.bonus && parseInt(bet.bonus) > 0 && renderMultibetBonus}
                <span className={styles.label}>
                    <Translate tid="account_bets_picks" namespace={TranslationScopes.UserAccount} />
                    :
                </span>
                {bet.bets.length}
            </div>
            <div>
                <p>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_stake"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <strong>
                        <CurrencyFormatter
                            currency={bet.currency}
                            amount={bet.amount}
                            withCode={false}
                            withSymbol
                        />
                    </strong>
                </p>
                <p>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_posWin"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <strong>
                        <CurrencyFormatter
                            currency={bet.currency}
                            amount={bet.possible_win}
                            withCode={false}
                            withSymbol
                        />
                    </strong>
                    <strong className={styles.odds}>
                        <span>
                            <Translate
                                tid="account_bets_totalOdds"
                                namespace={TranslationScopes.UserAccount}
                            />
                            :
                        </span>
                        {bet.odds}
                    </strong>
                </p>
            </div>
            <div className={styles.return}>
                {/* <strong>Bonus</strong> */}
                <span className={styles.label}>
                    <Translate
                        tid="account_bets_result"
                        namespace={TranslationScopes.UserAccount}
                    />
                    :
                </span>
                {bet.status === TicketStatus.Accepted ? (
                    "- -"
                ) : (
                    <CurrencyFormatter
                        currency={bet.currency}
                        amount={bet.payout}
                        withCode={false}
                        withSymbol
                    />
                )}
            </div>
            {showDetailsButton && (
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/arrow-white.svg`}
                    className={classNames("svg-icon", styles.detailsBtnVertical)}
                />
            )}
        </div>
    );
};

export default SportOtherBetView;
